import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, UploadCloud, Download, FileText, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { parseCSV } from '../../utils/csv';

export default function CsvSetupModal({ isOpen, onClose, onStartImport, importType = 'prequalifications' }) {
    const [file, setFile] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            analyzeFile(selectedFile);
        }
    };

    const analyzeFile = (file) => {
        setFile(file);
        setIsAnalyzing(true);
        const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

        if (isExcel) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                try {
                    const data = new Uint8Array(evt.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
                    const count = Math.max(0, rows.length - 1);
                    setRowCount(count);
                } catch (err) {
                    console.error('Error analyzing Excel file:', err);
                } finally {
                    setIsAnalyzing(false);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            const reader = new FileReader();
            reader.onload = (evt) => {
                try {
                    const rows = parseCSV(evt.target.result);
                    const count = Math.max(0, rows.length - 1);
                    setRowCount(count);
                } catch (err) {
                    console.error('Error analyzing CSV file:', err);
                } finally {
                    setIsAnalyzing(false);
                }
            };
            reader.readAsText(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
            analyzeFile(droppedFile);
        }
    };

    const downloadTemplate = () => {
        let headers, filename, dataRows = [];
        if (importType === 'contacts') {
            headers = ["First Name", "Last Name", "Phone", "Email", "Notes"];
            filename = "contacts_template.csv";
        } else if (importType === 'users') {
            headers = ["Name", "Email", "Role", "Report To"];
            filename = "users_template.csv";
            dataRows = [
                ["ceo", "ceo@example.com", "CEO", ""],
                ["gm_ops", "gm@example.com", "GM Operations", "ceo@example.com"],
                ["senior_tl", "stl@example.com", "Senior Team Lead", "gm@example.com"],
                ["team_lead", "tl@example.com", "Team Lead", "gm@example.com"],
                ["associate_tl", "assoc@example.com", "Associate Team Lead", "tl@example.com"]
            ];
        } else if (importType === 'profiles') {
            headers = [
                "Created Date", "Project Name", "Client Name", "Contact Person Name", "Designation", 
                "Contact Detail Ext", "Mobile", "Email", "City", "Address", "Source", "Status", 
                "Assignee", "Approval Status", "Approval Remarks", "Notes", "Follow Up Date"
            ];
            filename = "profiles_template.csv";
            dataRows = [
                ["21-09-2026", "Project Alpha", "Acme Corporation", "John Doe", "Procurement Manager", "Ext 102", "03001234567", "john.doe@example.com", "Karachi", "123 Business St, Clifton", "Website", "New", "admin", "Approved", "Verified by GM Operations", "Initial profile recorded via website", "05-10-2026"],
                ["22-09-2026", "Project Beta", "Vertex Global", "Sarah Jenkins", "Operations Director", "Ext 205", "03009876543", "sarah@vertex.com", "Lahore", "Plot 45, Gulberg III", "Referral", "In Process", "admin", "Pending Approval", "", "Follow-up requested regarding service requirements", "15-10-2026"],
                ["23-09-2026", "Project Gamma", "Prime Health", "Dr. Asim", "CEO", "", "03331234567", "asim@primehealth.com", "Islamabad", "Blue Area", "Direct Call", "Interested", "admin", "Approved", "", "Ready for Prequalification / Tender proposal", "08-10-2026"]
            ];
        } else if (importType === 'tenders') {
            headers = [
                "Created Date", "Tender Title", "Client Name", "Contact Person Name", "Designation", "Mobile", "Email", "City", "Address", 
                "Tender Type", "Status", "Proposal Value", "Negotiated Amount", "Gross Profit", "WHT", "Mobilization Advance", 
                "Contract End Date / Duration", "No of Staff", "Earnest Money Value", "Payment Terms", "Source", "Assignee", 
                "Approval Status", "Approval Remarks", "Services", "Notes", "Awarded On", "Follow Up Date"
            ];
            filename = "tenders_template.csv";
            dataRows = [
                ["21-09-2026", "HVAC Maintenance 2026", "Tech Solutions", "Ali Khan", "Director", "03009876543", "info@tech.com", "Lahore", "Gulberg", "TENDER", "In Process", "1500000", "1450000", "200000", "10", "50000", "21-09-2027", "5", "50000", "Net 30", "Website", "Ahmed", "Approved", "Approved by CEO", "HVAC Maintenance", "High priority", "01-11-2026", "05-10-2026"],
                ["22-09-2026", "Facility Cleaning Proposal", "Apex Logistics", "Bilal Tariq", "Manager Admin", "03211234567", "bilal@apex.com", "Karachi", "Port Qasim", "PROPOSAL", "In Process", "850000", "800000", "120000", "8", "25000", "31-03-2027", "8", "25000", "Net 15", "Referral", "Ahmed", "Pending Approval", "", "Cleaning Services", "Proposal submitted for review", "", "15-10-2026"]
            ];
        } else if (importType === 'contracts') {
            headers = [
                "Contract #", "Client Name", "Phone", "Email", "Contact Person Name", "Designation", 
                "City", "Address", "Project / Title", "Service Type", "Start Date", "End Date", 
                "Contract Duration", "Total Value", "Collected", "Payment Status", "Payment Terms", 
                "Status", "Assignee", "Remarks"
            ];
            filename = "contracts_template.csv";
            dataRows = [
                ["CTR-2026-001", "Al-Falah Bank", "03001234567", "info@alfalah.com", "Ali Khan", "Procurement Head", "Karachi", "I.I. Chundrigar Road", "Head Office FM Services", "Complete FM", "01-01-2026", "31-12-2026", "1 Year", "2400000", "600000", "Partial", "Monthly Net 30", "Active", "admin", "HVAC and general facility maintenance"],
                ["CTR-2026-002", "Engro Polymer", "03219876543", "engro@example.com", "Bilal Tariq", "Plant Director", "Lahore", "Port Qasim Site", "Plant MEP Maintenance", "MEP", "15-02-2026", "14-02-2027", "1 Year", "1800000", "1800000", "Paid", "Quarterly Advance", "Active", "admin", "Annual preventive MEP contract"]
            ];
        } else {
            headers = [
                "Created Date", "Project Name", "Client Name", "Contact Person Name", "Designation", "Mobile", "Email", "City", "Address", 
                "Earnest Money", "Doc Submission Status", "Submission Date", "Source", "Status", 
                "Assignee", "Approval Status", "Approval Remarks", "Enlisted On", "Services", "Notes", "Follow Up Date"
            ];
            filename = "prequalifications_template.csv";
            dataRows = [
                ["21-09-2026", "Project X", "Tech Solutions", "Ali Khan", "Director", "03009876543", "info@tech.com", "Lahore", "Gulberg", "50000", "To be Received", "01-10-2026", "Website", "New", "Ahmed", "Approved", "Verified by GM Operations", "", "HVAC Maintenance", "High priority", "05-10-2026"],
                ["22-09-2026", "Security Services Prequalification", "Omega Towers", "Kamran Shah", "Head of Security", "03331122334", "kamran@omega.com", "Islamabad", "Blue Area", "100000", "In Process", "15-10-2026", "Direct Call", "New", "Ahmed", "Pending Approval", "", "", "Security Guard Services", "Awaiting document verification", "12-10-2026"]
            ];
        }
        
        const escapeCsvCell = (val) => {
            const str = String(val ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCsvCell).join(","),
            ...dataRows.map(row => row.map(escapeCsvCell).join(","))
        ].join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const modalContent = (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-slate-50">
                    <h3 className="font-bold text-gray-900 text-lg">
                        {importType === 'contacts' ? 'Import Contacts' : (importType === 'users' ? 'Import Team Members' : (importType === 'profiles' ? 'Import Profiles' : (importType === 'tenders' ? 'Import Tenders / Proposals' : (importType === 'contracts' ? 'Import Contracts' : 'Import Prequalifications'))))}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-6">
                    {/* Template Download */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-semibold text-blue-900">Need a template?</p>
                            <p className="text-xs text-blue-700 mt-0.5">Download our sample CSV format (DD-MM-YYYY)</p>
                        </div>
                        <button 
                            onClick={downloadTemplate}
                            className="flex items-center gap-2 bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                        >
                            <Download className="w-4 h-4" />
                            Template
                        </button>
                    </div>

                    {/* Upload Area */}
                    {!file ? (
                        <div 
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors"
                        >
                            <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-3">
                                <UploadCloud className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-semibold text-gray-700">Click to upload or drag and drop</p>
                            <p className="text-xs text-gray-500 mt-1">CSV or Excel files (.csv, .xlsx, .xls) (Max 10MB)</p>
                            <input 
                                type="file" 
                                className="hidden" 
                                accept=".csv, .xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv" 
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-2xl p-6 bg-slate-50 flex flex-col items-center justify-center text-center relative">
                            <button 
                                onClick={() => setFile(null)}
                                className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-3">
                                <FileText className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-bold text-gray-800 truncate max-w-full px-4">{file.name}</p>
                            
                            {isAnalyzing ? (
                                <div className="flex items-center gap-2 mt-2 text-blue-600 text-xs font-medium">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Analyzing file...
                                </div>
                            ) : (
                                <div className="mt-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm flex items-center gap-2 text-sm">
                                    <span className="text-gray-500">Total {importType === 'contacts' ? 'contacts' : (importType === 'users' ? 'team members' : (importType === 'profiles' ? 'profiles' : (importType === 'tenders' ? 'tenders' : 'prequalifications')))} found:</span>
                                    <span className="font-bold text-emerald-600">{rowCount}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => file && onStartImport(file)}
                        disabled={!file || isAnalyzing || rowCount === 0}
                        className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
                    >
                        Start Import
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}
